import * as React from "react";
import { useLocation } from "react-router";

type RouteLabels = Readonly<Record<string, string | undefined>>;
const emptyLabels: RouteLabels = Object.freeze({});
interface RouteLabelsContextValue {
    labels: RouteLabels;
    setLabel: (key: string, value: string | undefined) => void;
}

// Page components are also mounted directly by a number of integration/editor
// harnesses, outside the AppLayout subtree.  Those mounts do not have a route
// metadata synchronizer to consume labels, so publishing a label there is a
// harmless no-op.  Keep the fallback stable so these mounts do not throw while
// the application shell continues to use the stateful provider below.
const fallbackRouteLabels: RouteLabelsContextValue = Object.freeze({
    labels: Object.freeze({}),
    setLabel: () => undefined,
});

const RouteLabelsContext = React.createContext<RouteLabelsContextValue>(fallbackRouteLabels);

export const RouteLabelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const routeKey = `${location.key}:${location.pathname}`;
    const [state, setState] = React.useState<{ routeKey: string; labels: Record<string, string | undefined> }>(
        () => ({ routeKey, labels: {} }),
    );
    if (state.routeKey !== routeKey) {
        setState({ routeKey, labels: {} });
    }
    const labels = state.routeKey === routeKey ? state.labels : emptyLabels;
    const setLabel = React.useCallback((key: string, value: string | undefined) => {
        setState(current => {
            const currentLabels = current.routeKey === routeKey ? current.labels : {};
            if (currentLabels[key] === value) return current.routeKey === routeKey ? current : { routeKey, labels: currentLabels };
            return { routeKey, labels: { ...currentLabels, [key]: value } };
        });
    }, [routeKey]);
    const value = React.useMemo(() => ({ labels, setLabel }), [labels, setLabel]);
    return <RouteLabelsContext.Provider value={value}>{children}</RouteLabelsContext.Provider>;
};

export const useRouteLabels = (): RouteLabelsContextValue => {
    return React.useContext(RouteLabelsContext);
};

export default RouteLabelsContext;
